import { HStack, VStack, Box } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import Section from "./Section";
import DarkHeader from "src/layout/DarkHeader";
import {
  AFFIRMATION_ROUTE,
  COMMUNICATION_ROUTE,
  FREQUENCY_ROUTE,
  GATE_ROUTE,
  PREPARATION_ROUTE,
} from "src/routePaths";
import {
  PreparationSection,
  AffirmationsSection,
  CommunicationSection,
  FrequenciesSection,
  GatesSection,
} from "./Sections";
import Calibration from "../Affirmation/Calibration";
import { useAppContext } from "../../Providers";
import { useDispatch, useSelector } from "src/store";
import { update } from "src/store/auth";
import { Redirect } from "react-router-dom";
import { get } from "src/api";
import { fetchAuthUserMe } from "src/api/subscription";
import RedesignLifeButton from "./RedesignLifeButton";
import LightThanosBar from "src/components/ThanosBar/LightThanosBar";
import ASButton from "./ASButton";
import BioFlowButton from "./BioFlowButton";
import useViewPortWidth from "src/utils/use-viewport-width";
import useLocale from "src/providers/useLocale";
import {
  getButtonSize,
  getLeftButtonTransform,
  getRightButtonTransform,
  getRightButtonJustification,
  getMobileButtonTransform,
  getButtonByPosition,
} from "./portalUtils";

// Constants
const BACKGROUND_IMAGE_PATH = 'url("/main-page-background7.jpg")';
const NOTIFICATION_API_ENDPOINT = "/shared/notification/all";

// const BACKGROUND_STYLES = {
//   position: "fixed" as const,+

//   top: 0,
//   left: 0,
//   minWidth: "100%",
//   minHeight: "100vh",
//   backgroundSize: "cover",
//   backgroundAttachment: "fixed",
//   backgroundPosition: "center center",
//   backgroundRepeat: "no-repeat",
//   zIndex: -100,
//   opacity: 1,
// };

const BACKGROUND_STYLES = {
  position: "fixed" as const, // Anchored to a relatively positioned parent
  top: 0,
  left: 0,
  width: "100%",
  height: "100vh", // Ensures it grows with scrollable content
  backgroundSize: "cover",
  backgroundPosition: "center center",
  backgroundRepeat: "no-repeat",
  zIndex: -1, // Behind all content
  opacity: 1,
  backgroundColor: "#f3f3f3", // Fallback in case image fails to load
  pointerEvents: "none", // Prevents interaction issues
};

const Portal: React.FC<{}> = () => {
  // State and hooks
  const [hasVideoLoaded, setVideoLoaded] = React.useReducer(() => true, false);
  const [notificationData, setNotificationData] = useState<any>({});
  const { viewPortWidth } = useViewPortWidth();
  const [locale] = useLocale();

  const appContext = useAppContext();
  const dispatch = useDispatch();

  // Selectors
  const currentUser = useSelector((state) => state.auth.user);

  // Language and screen size logic
  const isLargeScreen = viewPortWidth >= 920;
  const isArabic = locale === "ar";
  const buttonConfig = getButtonSize(viewPortWidth);

  // Effects
  useEffect(() => {
    setVideoLoaded();
  }, []);

  useEffect(() => {
    // Set background image
    appContext.background?.set(BACKGROUND_IMAGE_PATH);

    // Fetch notifications
    get(NOTIFICATION_API_ENDPOINT).then((response: any) => {
      if (response) {
        setNotificationData(response);
      }
    });

    // Cleanup background on unmount
    return () => {
      appContext.background?.set("");
    };
  }, [appContext.background]);

  useEffect(() => {
    const refreshUserData = async () => {
      if (!currentUser?.accessToken) return;

      try {
        const freshUserData = await fetchAuthUserMe(locale);
        if (freshUserData?.id) {
          dispatch(
            update({
              user: { ...freshUserData, accessToken: currentUser.accessToken },
            })
          );
        }
      } catch {
        // Fail silently; feature locks fall back to last known Redux state
      }
    };

    refreshUserData();
  }, [currentUser?.accessToken, dispatch, locale]);

  // Refresh again after returning from external payment tabs (webhook lag)
  useEffect(() => {
    const askSalahReturn = sessionStorage.getItem("askSalahPaymentPending");
    const bioflowReturn = sessionStorage.getItem("bioflowPaymentPending");
    if (!askSalahReturn && !bioflowReturn) return;

    sessionStorage.removeItem("askSalahPaymentPending");
    sessionStorage.removeItem("bioflowPaymentPending");

    const refreshTimer = setTimeout(async () => {
      if (!currentUser?.accessToken) return;

      try {
        const freshUserData = await fetchAuthUserMe(locale);
        if (freshUserData?.id) {
          dispatch(
            update({
              user: { ...freshUserData, accessToken: currentUser.accessToken },
            })
          );
        }
      } catch {
        // Fail silently
      }
    }, 3000);

    return () => clearTimeout(refreshTimer);
  }, [currentUser?.accessToken, dispatch, locale]);

  // Early returns for authentication and subscription status
  if (!currentUser) {
    return <Redirect to={"/"} />;
  }

  if (
    currentUser?.pendingOrder &&
    currentUser?.subscriptionStatus === "pending"
  ) {
    return <Redirect to={"/subscription"} />;
  }

  // Render helpers
  const renderBackgroundLayer = () => (
    <>
      <div
        style={{
          ...BACKGROUND_STYLES,
          backgroundImage: appContext.background.get,
        }}
      />
      {/* Dark overlay to make background darker */}
      {/* <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          minWidth: "100%",
          minHeight: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.1)",
          zIndex: -99,
        }}
      /> */}
    </>
  );

  const renderVideoLayer = () => (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        minWidth: "100%",
        minHeight: "100vh",
        zIndex: -50,
        opacity: 0.975,
      }}
    >
      {/* Video background layer - currently commented out
      {hasVideoLoaded ? (
        <video
          id="backgroundVideo"
          preload="none"
          onContextMenu={(e) => e.preventDefault()}
          controlsList="nodownload"
          src="/videos/SalAnimationFinal2.mp4"
          autoPlay
          muted
          playsInline
          loop
          style={{
            position: "fixed",
            objectFit: "cover",
            minWidth: "100%",
            minHeight: "100%",
            top: 0,
            left: 0,
            pointerEvents: "none",
          }}
          controls
        />
      ) : null} */}
    </div>
  );

  const renderThanosBarSection = () => (
    <Box position="relative">
      {/* Mobile Layout (< 768px) - Vertical Stack */}
      <Box display={{ base: "block", md: "none" }}>
        {/* ThanosBar Section - Light Gem Buttons */}
        {currentUser?.subscriptionStatus === "approved" && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            marginBottom="1.8em"
          >
            <LightThanosBar />
          </Box>
        )}

        {/* Two Buttons Section - Below ThanosBar */}
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          paddingX="6.5em"
          alignItems="center"
          width="100%"
          maxWidth="500px"
          margin="0 auto"
          marginBottom="1.8em"
          dir="ltr"
        >
          {/* First Button - Arabic: AS | English: RedesignLife */}
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            transform={getMobileButtonTransform(buttonConfig)}
          >
            {getButtonByPosition(true, isArabic, ASButton, RedesignLifeButton)}
          </Box>

          {/* Second Button - Arabic: RedesignLife | English: AS */}
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            transform={getMobileButtonTransform(buttonConfig)}
          >
            {getButtonByPosition(false, isArabic, ASButton, RedesignLifeButton)}
          </Box>
        </Box>
      </Box>

      {/* Desktop Layout (>= 768px) - Original Horizontal Layout */}
      <Box display={{ base: "none", md: "block" }}>
        <Box
          position="relative"
          left="50%"
          transform="translateX(-50%)"
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          width="100%"
          maxWidth={buttonConfig.maxWidth}
          height="100%"
          gap={buttonConfig.gap}
          dir="ltr" // Force LTR direction to prevent button swapping
        >
          {/* Left Position - Arabic: AS | English: RedesignLife */}
          <Box
            display="flex"
            justifyContent="start"
            alignItems="center"
            flexShrink={0}
            flex={1}
            order={1}
            transform={getLeftButtonTransform(
              isArabic,
              isLargeScreen,
              buttonConfig
            )}
          >
            {getButtonByPosition(true, isArabic, ASButton, RedesignLifeButton)}
          </Box>

          {/* ThanosBar - Center position, only visible for approved users */}
          {currentUser?.subscriptionStatus === "approved" && (
            <Box flex={1} order={2} transform={`scale(${buttonConfig.scale})`}>
              <LightThanosBar />
            </Box>
          )}

          {/* Right Position - Arabic: RedesignLife | English: AS */}
          <Box
            flex={1}
            order={3}
            display="flex"
            justifyContent={getRightButtonJustification(isArabic)}
            alignItems="center"
            transform={getRightButtonTransform(
              isArabic,
              isLargeScreen,
              buttonConfig
            )}
          >
            {getButtonByPosition(false, isArabic, ASButton, RedesignLifeButton)}
          </Box>
        </Box>
      </Box>

      {/* SALBioFlow entry */}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        marginTop={{ base: "1rem", md: "0.75rem" }}
        marginBottom={{ base: "0.5rem", md: "0" }}
      >
        <Box transform={`scale(${buttonConfig.scale})`}>
          <BioFlowButton />
        </Box>
      </Box>
    </Box>
  );

  const renderMainSections = (): JSX.Element => (
    // @ts-ignore
    <HStack
      flexDirection={["column", "column", "row", "row", "row"]}
      alignItems={["center", "center", "center", "center", "flex-start"]}
      style={{
        justifyContent: "center",
        margin: "0 auto",
        position: "relative",
      }}
      maxWidth="100%"
      paddingX={["0.5em", "1em", "1em", "1em", "0"]}
      minWidth={["300px", "auto", "auto", "auto", "auto"]}
      width="100%"
    >
      {/* Left Column - Preparation */}
      <Section
        title="preparation"
        slug="Preparation"
        categories={PreparationSection}
        notifications={notificationData}
        mainRoute={PREPARATION_ROUTE}
      />

      {/* Middle Column - Affirmation & Communication */}
      <VStack>
        <Section
          title="affirmation"
          slug="Affirmation"
          notifications={notificationData}
          categories={AffirmationsSection}
          mainRoute={AFFIRMATION_ROUTE}
        />
        <Section
          title="communication"
          slug="Communication"
          notifications={notificationData}
          categories={CommunicationSection}
          mainRoute={COMMUNICATION_ROUTE}
        />
      </VStack>

      {/* Right Column - Frequency & Gates */}
      <VStack>
        <Section
          title="frequency"
          slug="Frequencies"
          notifications={notificationData}
          categories={FrequenciesSection}
          mainRoute={FREQUENCY_ROUTE}
        />
        <Section
          title="gate"
          slug="Gates"
          notifications={notificationData}
          categories={GatesSection}
          mainRoute={GATE_ROUTE}
        />
      </VStack>
    </HStack>
  );

  return (
    <>
      {/* Background layers */}
      {renderBackgroundLayer()}
      {renderVideoLayer()}

      {/* Main content */}
      <Box backgroundSize={"100%"} paddingBottom="50px">
        <DarkHeader />

        {/* Ask Salah and Redesign Life buttons section */}
        <Box
          position="relative"
          left="50%"
          transform="translateX(-50%)"
          width={["100%", "100%", "100%"]}
          maxWidth={["none", "none", "100%"]}
          paddingX={["0.5em", "1em", "0"]}
          marginTop={{
            base: "1.8em",
            md: "2em",
          }}
        >
          {renderThanosBarSection()}
        </Box>

        {/* Main sections container */}
        <Box
          position="relative"
          marginTop={{
            base: "0",
            md: "3em",
          }}
        >
          {renderMainSections()}
        </Box>

        {/* Calibration component */}
        <Calibration />
      </Box>
    </>
  );
};

export default Portal;
